import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Gem,
  ArrowRight,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Sparkles,
  BookOpen,
  Plane,
  Lightbulb,
  Share2,
  Layers,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SEO from "@/components/SEO";

// -----------------------------------------------------------------------------
// Postmoro Login / Sign Up screen — secure password edition
// -----------------------------------------------------------------------------
// One screen, two modes (Log in / Sign up). Both flows use real password auth.
//   * Sign up  -> POST /api/auth/signup  (email + business name + password + confirm)
//   * Log in   -> POST /api/auth/login   (email + password)
//   * Google   -> Emergent Managed OAuth (still supported, real flow)
//
// Legacy passwordless accounts get a 409 password_setup_required on login —
// we surface a friendly "Please reset your password" path that points users
// to support so the admin can issue a one-time reset link.
// -----------------------------------------------------------------------------

const MODES = {
  login: {
    title: "Welcome back",
    subtitle: "Log in to manage your AI-powered social content.",
    cta: "Log in",
    loadingCta: "Logging you in…",
    switchPrompt: "New to Postmoro?",
    switchLabel: "Create an account",
    toastSuccess: "Welcome back!",
  },
  signup: {
    title: "Create your Postmoro account",
    subtitle:
      "Set up your AI marketing assistant in minutes. No credit card required.",
    cta: "Create account",
    loadingCta: "Creating your account…",
    switchPrompt: "Already have an account?",
    switchLabel: "Log in",
    toastSuccess: "Welcome to Postmoro!",
  },
};

const BENEFITS = [
  {
    icon: Sparkles,
    title: "Create on-brand content",
    desc: "Generate AI-powered marketing content shaped around your business, audience, and brand voice.",
  },
  {
    icon: Plane,
    title: "Automate recurring work",
    desc: "Use Autopilot to keep content workflows moving without starting from scratch every time.",
  },
  {
    icon: BookOpen,
    title: "Learn your brand",
    desc: "Brand Learning helps Postmoro understand how your business communicates so AI output becomes more relevant.",
  },
  {
    icon: Lightbulb,
    title: "Generate marketing ideas",
    desc: "Turn your business context into practical ideas for reaching and engaging customers.",
  },
  {
    icon: Share2,
    title: "Publish across social platforms",
    desc: "Create, schedule, and publish to your connected social accounts from one workspace.",
  },
  {
    icon: Layers,
    title: "Manage everything in one place",
    desc: "Keep your content, media, business profile, calendar, and connected platforms organized inside Postmoro.",
  },
];

function passwordIssues(pw) {
  // Mirrors the backend rules in _validate_password_strength.
  const issues = [];
  if (!pw || pw.length < 8) issues.push("At least 8 characters");
  if (!/[A-Za-z]/.test(pw || "")) issues.push("At least 1 letter");
  if (!/\d/.test(pw || "")) issues.push("At least 1 number");
  return issues;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [errorDetails, setErrorDetails] = useState(null);
  const [needsReset, setNeedsReset] = useState(false);

  const copy = MODES[mode];

  const debugEnabled =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("debug") === "1";
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    if (!debugEnabled) return;
    let cancelled = false;
    (async () => {
      const probe = async (path) => {
        const url = `${API_BASE}${path}`;
        const started = performance.now();
        try {
          const res = await fetch(url, { method: "GET", credentials: "include" });
          let body = null;
          try { body = await res.json(); } catch (_e) { body = await res.text().catch(() => null); }
          return { url, ok: res.ok, status: res.status, ms: Math.round(performance.now() - started), body };
        } catch (e) {
          return { url, ok: false, status: "network-error", ms: Math.round(performance.now() - started), error: String(e?.message || e) };
        }
      };
      const [health, echo] = await Promise.all([probe("/health"), probe("/_debug/echo")]);
      if (!cancelled) {
        setDebugInfo({
          api_base: API_BASE,
          window_origin: typeof window !== "undefined" ? window.location.origin : null,
          window_href: typeof window !== "undefined" ? window.location.href : null,
          health, echo,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [debugEnabled]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  // ------------------------------------------------------------------
  // Postmoro-branded Google OAuth (Google Cloud verification COMPLETE).
  //
  // Gating is now driven by the backend readiness probe alone:
  //   /api/auth/google/config  → { ready: true|false }
  //
  // The backend's own host-consistency guard + allowlist is
  // authoritative: it only reports ``ready:true`` when the request host
  // is one of ``_CUSTOM_GOOGLE_ALLOWED_HOSTS`` AND the two real secrets
  // (GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET) are present. Unknown or
  // wrong-environment hosts stay disabled.
  //
  // Emergent Managed Auth (``handleGoogle`` above) is retained as a
  // rollback path in case the branded flow needs to be toggled off —
  // it is not shown to users when the branded button is ready.
  // ------------------------------------------------------------------
  const [customGoogleReady, setCustomGoogleReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/auth/google/config");
        if (!cancelled) setCustomGoogleReady(!!res?.data?.ready);
      } catch (_e) {
        if (!cancelled) setCustomGoogleReady(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const handleCustomGoogle = () => {
    // Full-page redirect to backend start route — no client-side query
    // building, no client secret exposure, no dynamic redirect_uri.
    window.location.href = `${API_BASE}/auth/google/start`;
  };
  const showCustomGoogle = customGoogleReady;

  const pwIssues = useMemo(() => passwordIssues(password), [password]);
  const showPwRules = mode === "signup" && password.length > 0 && pwIssues.length > 0;
  const confirmMismatch = mode === "signup" && confirm.length > 0 && confirm !== password;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setErrorMsg("");
    setErrorDetails(null);
    setNeedsReset(false);
    if (!email) { setErrorMsg("Please enter your email address."); return; }
    if (!password) { setErrorMsg("Please enter your password."); return; }
    if (mode === "signup") {
      if (!firstName) { setErrorMsg("Please enter your first name."); return; }
      if (!businessName) { setErrorMsg("Please enter your business name."); return; }
      if (passwordIssues(password).length) {
        setErrorMsg("Please choose a stronger password."); return;
      }
      if (confirm !== password) {
        setErrorMsg("Passwords do not match."); return;
      }
    }

    setLoading(true);
    try {
      const path = mode === "signup" ? "/auth/signup" : "/auth/login";
      const body = mode === "signup"
        ? { email, password, confirm_password: confirm, first_name: firstName, business_name: businessName, marketing_email_opt_in: marketingOptIn }
        : { email, password };
      const res = await api.post(path, body);
      // NOTE (Phase A2): the backend no longer returns session_token
      // in the JSON body. Authentication is carried entirely by the
      // HttpOnly cookie set on this response. Do not read, store, or
      // forward any token here.
      // Iteration 80 Phase 2 — if the user has TOTP 2FA enabled the
      // session is now in the ``mfa_pending`` state; route them to
      // /login/2fa instead of /dashboard until the TOTP verify passes.
      if (res.data?.mfa_required) {
        // Set a lightweight partial user so Protected knows we're in
        // the pending state. auth.js.refresh() will replace it with
        // the canonical /auth/me shape after verification.
        setUser({ mfa_required: true, email: res.data?.user?.email || email });
        toast.success("Almost there — enter your 2FA code.");
        navigate("/login/2fa", { replace: true });
        return;
      }
      setUser(res.data?.user || null);
      toast.success(copy.toastSuccess);
      // <Protected> handles routing to /onboarding if first login.
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const status = err?.response?.status ?? "network";
      const data = err?.response?.data;
      const serverMsg =
        (data && typeof data === "object" && (data.detail || data.message)) ||
        (typeof data === "string" ? data : null) ||
        err?.message ||
        "Unknown error";
      const requestUrl = (err?.config?.baseURL || "") + (err?.config?.url || "");
      const reqIdMatch = typeof serverMsg === "string" && serverMsg.match(/req\s+([a-f0-9]{6,})/i);
      const details = {
        status,
        request_url: requestUrl,
        server_message: typeof serverMsg === "string" ? serverMsg : JSON.stringify(serverMsg),
        request_id: reqIdMatch ? reqIdMatch[1] : null,
      };
      if (status === 409 && typeof serverMsg === "string" && serverMsg === "password_setup_required") {
        setNeedsReset(true);
        setErrorMsg("This account doesn’t have a password yet. Please contact support to receive a reset link.");
      } else if (status === 429) {
        // Prefer the backend's specific, user-friendly reason when present
        // (e.g. the free-account network guard). Otherwise fall back to a
        // MODE-AWARE message so signup never shows "login" wording.
        const detail = data && typeof data === "object" ? data.detail : null;
        const backendMsg =
          (detail && typeof detail === "object" && typeof detail.message === "string" && detail.message) ||
          (typeof detail === "string" && detail.length < 200 && !/^[a-z0-9_]+$/i.test(detail) ? detail : null);
        setErrorMsg(
          backendMsg ||
            (mode === "signup"
              ? "Too many account creation attempts. Please wait a few minutes and try again."
              : "Too many login attempts. Please wait a few minutes and try again."),
        );
      } else if (status === "network") {
        setErrorMsg("We couldn’t reach Postmoro. Please check your connection and try again.");
      } else if (typeof serverMsg === "string" && serverMsg.length < 200) {
        setErrorMsg(serverMsg);
      } else {
        setErrorMsg(mode === "signup" ? "Couldn’t create account." : "Couldn’t log you in.");
      }
      setErrorDetails(details);
      // eslint-disable-next-line no-console
      console.error(`[postmoro] auth/${mode} failed`, details, err);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode((m) => (m === "login" ? "signup" : "login"));
    setErrorMsg("");
    setErrorDetails(null);
    setNeedsReset(false);
  };

  return (
    <div
      className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] relative overflow-hidden"
      data-testid="auth-page"
      data-mode={mode}
    >
      <SEO
        title="Log in | Postmoro"
        description="Postmoro is an AI business growth assistant for content creation, automation, publishing, brand learning, marketing ideas, and social media management."
        path="/login"
        noindex
      />
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[color:var(--brand-primary)] opacity-[0.08] blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-[color:var(--brand-primary)] opacity-[0.05] blur-3xl pointer-events-none" />

      <header className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="login-logo">
          <div className="h-7 w-7 rounded-md bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] flex items-center justify-center">
            <Gem className="h-4 w-4" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">Postmoro</span>
        </Link>
        <Link
          to="/"
          className="text-sm text-[color:var(--text-2)] hover:text-[color:var(--text-1)] transition"
          data-testid="auth-back-home"
        >
          Back to home
        </Link>
      </header>

      <main className="relative">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <motion.aside
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-6 hidden lg:block"
            data-testid="auth-benefits"
          >
            <div className="eyebrow mb-3">AI-Powered Business Growth</div>
            <h2 className="font-display text-3xl xl:text-4xl font-semibold tracking-tight leading-tight">
              AI that learns your business and{" "}
              <span className="text-[color:var(--brand-primary)]">helps your marketing grow</span>.
            </h2>
            <p className="text-[color:var(--text-2)] mt-3 text-[15px] leading-relaxed">
              Postmoro brings content creation, automation, publishing, brand intelligence, and marketing ideas into one AI-powered workspace built around your business.
            </p>
            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {BENEFITS.map(({ icon: Icon, title, desc }) => (
                <li
                  key={title}
                  className="flex items-start gap-2.5 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]/60 px-3 py-2.5"
                  data-testid={`auth-benefit-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                >
                  <div className="h-7 w-7 rounded-md bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)] flex items-center justify-center shrink-0">
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[color:var(--text-1)] leading-snug">{title}</div>
                    <div className="text-[11px] text-[color:var(--text-3)] mt-0.5 leading-snug">{desc}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-7 text-xs text-[color:var(--text-3)] flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
              Free trial. No credit card required.
            </div>
          </motion.aside>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="lg:col-span-6 w-full"
          >
            <div className="mx-auto w-full max-w-[460px] rounded-2xl bg-[color:var(--surface-1)] border border-[color:var(--border-subtle)] shadow-[var(--shadow-card)] p-6 sm:p-7">
              {debugEnabled ? (
                <div className="mb-5 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-[11px] text-[color:var(--text-2)]" data-testid="auth-debug-panel">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-[color:var(--text-1)]">Diagnostic panel</span>
                    <span className="text-[10px] text-[color:var(--text-3)]">?debug=1</span>
                  </div>
                  {debugInfo ? (
                    <pre className="whitespace-pre-wrap break-all leading-snug">
{JSON.stringify(debugInfo, null, 2)}
                    </pre>
                  ) : (
                    <div className="text-[color:var(--text-3)]">Probing…</div>
                  )}
                </div>
              ) : null}

              <div
                className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] mb-6"
                data-testid="auth-mode-tabs"
              >
                {["login", "signup"].map((m) => {
                  const active = mode === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { setMode(m); setErrorMsg(""); setErrorDetails(null); setNeedsReset(false); }}
                      data-testid={`auth-mode-${m}`}
                      className={
                        "text-sm font-medium px-3 py-2 rounded transition " +
                        (active
                          ? "bg-[color:var(--surface-1)] text-[color:var(--text-1)] shadow-sm"
                          : "text-[color:var(--text-3)] hover:text-[color:var(--text-1)]")
                      }
                    >
                      {m === "login" ? "Log in" : "Sign up"}
                    </button>
                  );
                })}
              </div>

              <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="auth-title">{copy.title}</h1>
              <p className="text-sm text-[color:var(--text-2)] mt-1.5" data-testid="auth-subtitle">{copy.subtitle}</p>

              <button
                type="button"
                onClick={showCustomGoogle ? handleCustomGoogle : handleGoogle}
                data-testid={showCustomGoogle ? "auth-google-custom" : "auth-google"}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] hover:bg-[color:var(--surface-1)] transition px-4 py-2.5 text-sm font-medium text-[color:var(--text-1)]"
              >
                <span className="h-4 w-4 rounded-sm flex items-center justify-center text-[10px] font-bold text-white" style={{ background: "#EA4335" }}>G</span>
                Continue with Google
                <ArrowRight className="h-3.5 w-3.5 ml-1 text-[color:var(--text-3)]" />
              </button>


              <div className="my-4 flex items-center gap-3 text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">
                <div className="flex-1 h-px bg-[color:var(--border-subtle)]" />
                or continue with email
                <div className="flex-1 h-px bg-[color:var(--border-subtle)]" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="auth-form">
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">Email address</label>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="you@yourbusiness.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (errorMsg) setErrorMsg(""); }}
                    data-testid="auth-email"
                    className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)]"
                    required
                  />
                </div>

                {mode === "signup" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[color:var(--text-2)]">First name</label>
                      <Input
                        type="text"
                        autoComplete="given-name"
                        placeholder="e.g. Justin"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        data-testid="auth-first-name"
                        className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)]"
                        required
                        maxLength={60}
                      />
                      <div className="text-[10px] text-[color:var(--text-3)] mt-1">
                        We&apos;ll use this to greet you — not in your posts.
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-[color:var(--text-2)]">Business name</label>
                      <Input
                        type="text"
                        autoComplete="organization"
                        placeholder="e.g. Bella Pizza"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        data-testid="auth-business-name"
                        className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)]"
                        required
                      />
                      <div className="text-[10px] text-[color:var(--text-3)] mt-1">
                        Used in generated captions and content.
                      </div>
                    </div>
                  </div>
                ) : null}

                <div>
                  <label className="text-xs text-[color:var(--text-2)]">Password</label>
                  <div className="relative mt-1.5">
                    <Input
                      type={showPw ? "text" : "password"}
                      autoComplete={mode === "signup" ? "new-password" : "current-password"}
                      placeholder={mode === "signup" ? "Min 8 characters, with a letter and a number" : "Your password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="auth-password"
                      className="pr-9 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)]"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-3)] hover:text-[color:var(--text-1)]"
                      data-testid="auth-toggle-pw"
                      aria-label={showPw ? "Hide password" : "Show password"}
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {showPwRules ? (
                    <ul className="mt-2 text-[11px] text-[color:var(--text-3)] space-y-0.5" data-testid="auth-pw-rules">
                      {pwIssues.map((rule) => (<li key={rule}>\u2022 {rule}</li>))}
                    </ul>
                  ) : null}
                </div>

                {mode === "signup" ? (
                  <div>
                    <label className="text-xs text-[color:var(--text-2)]">Confirm password</label>
                    <Input
                      type={showPw ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Re-enter your password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      data-testid="auth-confirm"
                      className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)]"
                      required
                    />
                    {confirmMismatch ? (
                      <div className="mt-1 text-[11px] text-[color:var(--danger)]" data-testid="auth-confirm-mismatch">Passwords do not match.</div>
                    ) : null}
                  </div>
                ) : null}

                {mode === "signup" ? (
                  <label
                    className="flex items-start gap-2 text-[12px] text-[color:var(--text-2)] leading-relaxed cursor-pointer select-none"
                    data-testid="auth-marketing-optin-label"
                  >
                    <input
                      type="checkbox"
                      checked={marketingOptIn}
                      onChange={(e) => setMarketingOptIn(e.target.checked)}
                      className="mt-1 h-4 w-4 shrink-0 accent-[color:var(--brand-primary)] cursor-pointer"
                      data-testid="auth-marketing-optin"
                    />
                    <span>
                      Email me product updates, tips, feature announcements,
                      and occasional offers from Postmoro. I can unsubscribe
                      anytime.
                    </span>
                  </label>
                ) : null}

                {mode === "login" ? (
                  <div className="flex justify-end">
                    <Link
                      to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                      className="text-xs text-[color:var(--brand-primary)] hover:underline"
                      data-testid="auth-forgot"
                    >
                      Forgot password?
                    </Link>
                  </div>
                ) : null}

                {errorMsg ? (
                  <div
                    className="text-xs text-[color:var(--danger)] bg-[color:rgba(238,90,82,0.06)] border border-[color:rgba(238,90,82,0.30)] rounded-md px-3 py-2 space-y-2"
                    data-testid="auth-error"
                    role="alert"
                  >
                    <div className="font-medium">{errorMsg}</div>
                    {needsReset ? (
                      <div className="text-[color:var(--text-2)]">
                        Email <a href="mailto:support@postmoro.com" className="text-[color:var(--brand-primary)] hover:underline">support@postmoro.com</a> and we’ll send you a one-time reset link.
                      </div>
                    ) : null}
                    {errorDetails ? (
                      <details className="text-[color:var(--text-2)]" data-testid="auth-error-details">
                        <summary className="cursor-pointer text-[color:var(--text-2)] hover:text-[color:var(--text-1)] select-none">Show technical details</summary>
                        <pre className="mt-2 whitespace-pre-wrap break-all text-[11px] leading-snug text-[color:var(--text-2)]">
{JSON.stringify(errorDetails, null, 2)}
                        </pre>
                      </details>
                    ) : null}
                  </div>
                ) : null}

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="auth-submit"
                  className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                >
                  {loading ? (
                    <span className="inline-flex items-center" data-testid="auth-loading">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {copy.loadingCta}
                    </span>
                  ) : (
                    <>
                      {mode === "signup" ? <Mail className="h-4 w-4 mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                      {copy.cta}
                    </>
                  )}
                </Button>

                <p className="text-[11px] text-[color:var(--text-3)] leading-relaxed" data-testid="auth-legal">
                  By continuing, you agree to Postmoro’s{" "}
                  <Link to="/terms" className="text-[color:var(--text-2)] hover:underline">Terms of Service</Link> and{" "}
                  <Link to="/privacy" className="text-[color:var(--text-2)] hover:underline">Privacy Policy</Link>.
                </p>
              </form>

              <div className="mt-5 pt-4 border-t border-[color:var(--border-subtle)] text-center text-xs text-[color:var(--text-3)]">
                {copy.switchPrompt}{" "}
                <button
                  type="button"
                  onClick={switchMode}
                  data-testid="auth-switch-mode"
                  className="text-[color:var(--brand-primary)] font-medium hover:underline"
                >
                  {copy.switchLabel}
                </button>
              </div>
            </div>

            <div className="lg:hidden mt-8" data-testid="auth-benefits-mobile">
              <div className="text-xs uppercase tracking-wider text-[color:var(--text-3)] mb-3">What you get</div>
              <ul className="grid grid-cols-2 gap-2">
                {BENEFITS.slice(0, 4).map(({ icon: Icon, title }) => (
                  <li key={title} className="flex items-start gap-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]/60 px-2.5 py-2">
                    <Icon className="h-3.5 w-3.5 text-[color:var(--brand-primary)] mt-0.5 shrink-0" />
                    <span className="text-[11px] text-[color:var(--text-2)] leading-snug">{title}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
